import { formatHubBranchLabel } from './destinationType.js';
import { isLrShipmentFormat } from './shipmentIdFormat.js';

/** Same label as Add Shipment: `05 – Place` when Number sequence is on. */
export function formatLocationDropdownLabel(loc, useNumberCode = false) {
  const lr = String(loc?.lr_branch_code || '').trim();
  const place = String(loc?.location || formatHubBranchLabel(loc) || '').trim();
  if (useNumberCode && /^\d{2}$/.test(lr) && place) return `${lr} – ${place}`;
  const name = formatHubBranchLabel(loc);
  const code = (loc?.location_code || loc?.hub_code || '').trim();
  return code ? `${name} (${code})` : name;
}

export function locationMatchesQuery(loc, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  const name = String(loc?.location || '').toLowerCase();
  const code = String(loc?.location_code || loc?.hub_code || '').toLowerCase();
  const lr = String(loc?.lr_branch_code || '').toLowerCase();
  const label = formatHubBranchLabel(loc).toLowerCase();
  return name.includes(q) || code.includes(q) || lr.includes(q) || label.includes(q);
}

export function locationMatchRank(loc, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return 2;
  const name = String(loc?.location || '').toLowerCase();
  const code = String(loc?.location_code || loc?.hub_code || '').toLowerCase();
  const lr = String(loc?.lr_branch_code || '').toLowerCase();
  const label = formatHubBranchLabel(loc).toLowerCase();
  if (code === q || name === q || label === q || lr === q) return 0;
  if (code.startsWith(q) || name.startsWith(q) || label.startsWith(q) || lr.startsWith(q)) return 1;
  return 2;
}

/** Branch number used on the dropdown, such as 01 or 52. Missing numbers sort last. */
export function locationBranchNumber(loc) {
  const lr = String(loc?.lr_branch_code || '').trim();
  if (/^\d+$/.test(lr)) return Number(lr);
  return Number.POSITIVE_INFINITY;
}

export function compareLocationsByNumber(a, b) {
  const diff = locationBranchNumber(a) - locationBranchNumber(b);
  if (diff !== 0) return diff;
  return compareLocationsAsc(a, b);
}

/** Location name A–Z, then location code. */
export function compareLocationsAsc(a, b) {
  const nameA = String(a?.location || formatHubBranchLabel(a) || a?.hub_name || a?.name || '').trim();
  const nameB = String(b?.location || formatHubBranchLabel(b) || b?.hub_name || b?.name || '').trim();
  const byName = nameA.localeCompare(nameB, undefined, { sensitivity: 'base', numeric: true });
  if (byName !== 0) return byName;
  const codeA = String(a?.location_code || a?.hub_code || '').trim();
  const codeB = String(b?.location_code || b?.hub_code || '').trim();
  return codeA.localeCompare(codeB, undefined, { sensitivity: 'base', numeric: true });
}

export function sortLocationsAsc(locations) {
  return [...(Array.isArray(locations) ? locations : [])].sort(compareLocationsAsc);
}

export function sortLocationsByNumber(locations) {
  return [...(Array.isArray(locations) ? locations : [])].sort(compareLocationsByNumber);
}

export function filterLocationsByQuery(locations, query) {
  const list = Array.isArray(locations) ? locations : [];
  const q = String(query || '').trim();
  const matched = q ? list.filter((loc) => locationMatchesQuery(loc, q)) : list;
  return [...matched].sort((a, b) => {
    if (q) {
      const rank = locationMatchRank(a, q) - locationMatchRank(b, q);
      if (rank !== 0) return rank;
    }
    return compareLocationsAsc(a, b);
  });
}

export function useLrLocationLabels(shipmentIdFormat) {
  return isLrShipmentFormat(shipmentIdFormat);
}
